'use client';

import { Dialog } from '@base-ui/react/dialog';
import cn from 'classnames';
import { type ReactNode } from 'react';

import { Backdrop } from '../backdrop';
import { Body } from './body';
import { Footer } from './footer';
import { Header } from './header';
import './side-drawer.scss';

type Side = 'left' | 'right';

type SideDrawerProps = {
    children: ReactNode;
    initialFocus?: Dialog.Popup.Props['initialFocus'];
    isOpen: boolean;
    onOpenChange: NonNullable<Dialog.Root.Props['onOpenChange']>;
    onOpenChangeComplete?: Dialog.Root.Props['onOpenChangeComplete'];
    className?: string;
    onClose?: () => void;
    onOpen?: () => void;
    showBackdrop?: boolean;
    side?: Side;
};

function SideDrawerRoot({
    children,
    initialFocus,
    isOpen,
    onOpenChange,
    onOpenChangeComplete,
    className,
    onClose,
    onOpen,
    showBackdrop = true,
    side = 'left',
}: SideDrawerProps) {
    const handleOpenChange: NonNullable<Dialog.Root.Props['onOpenChange']> = (isOpening, eventDetails) => {
        isOpening ? onOpen?.() : onClose?.();

        onOpenChange(isOpening, eventDetails);
    };

    return (
        <Dialog.Root open={isOpen} onOpenChange={handleOpenChange} onOpenChangeComplete={onOpenChangeComplete}>
            <Dialog.Portal>
                {showBackdrop && <Dialog.Backdrop render={<Backdrop className="side-drawer__backdrop" />} />}
                <Dialog.Viewport className="side-drawer__viewport" data-side={side}>
                    <Dialog.Popup className={cn('side-drawer', className)} data-side={side} initialFocus={initialFocus}>
                        {children}
                    </Dialog.Popup>
                </Dialog.Viewport>
            </Dialog.Portal>
        </Dialog.Root>
    );
}

export const SideDrawer = Object.assign(SideDrawerRoot, {
    Body,
    Description: Dialog.Description, // For screen readers
    Footer,
    Header,
    Title: Dialog.Title, // For screen readers
});
